import { type Lead, LeadStatus } from '@up2stay/up2promo-api-client';
import { leadNameSortKey } from '@up2stay/up2promo-commons';

export type LeadsListSortOption =
  | 'createdDesc'
  | 'createdAsc'
  | 'updatedDesc'
  | 'nameAsc'
  | 'nameDesc'
  | 'statusAsc';

export const LEADS_LIST_SORT_OPTIONS: LeadsListSortOption[] = [
  'createdDesc',
  'createdAsc',
  'updatedDesc',
  'nameAsc',
  'nameDesc',
  'statusAsc',
];

/** Mobile filter-sheet sort chips (FilterMobile F1). */
export const LEADS_MOBILE_SHEET_SORT_OPTIONS: LeadsListSortOption[] = [
  'createdDesc',
  'createdAsc',
  'updatedDesc',
  'nameAsc',
];

const STATUS_SORT_ORDER: Record<LeadStatus, number> = {
  [LeadStatus.NEW]: 0,
  [LeadStatus.CONTACTED]: 1,
  [LeadStatus.QUALIFIED]: 2,
  [LeadStatus.WON]: 3,
  [LeadStatus.LOST]: 4,
};

/** Last name first; given name as tiebreaker. Falls back to email when name is empty. */
function leadSortName(lead: Lead): string {
  const sortKey = leadNameSortKey(lead.firstName, lead.lastName);
  if (sortKey) return sortKey;
  return (lead.email || '').trim();
}

function createdAtMs(lead: Lead): number {
  const raw = lead.createdAt;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function updatedAtMs(lead: Lead): number {
  const raw = lead.updatedAt;
  if (!raw) return createdAtMs(lead);
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : createdAtMs(lead);
}

export function sortLeadsForList(
  leads: Lead[],
  sort: LeadsListSortOption,
  locale: string,
): Lead[] {
  const collator = new Intl.Collator(locale, {
    sensitivity: 'base',
    numeric: true,
  });

  return [...leads].sort((a, b) => {
    switch (sort) {
      case 'createdAsc':
        return createdAtMs(a) - createdAtMs(b);
      case 'createdDesc':
        return createdAtMs(b) - createdAtMs(a);
      case 'updatedDesc':
        return updatedAtMs(b) - updatedAtMs(a);
      case 'nameAsc':
        return collator.compare(leadSortName(a), leadSortName(b));
      case 'nameDesc':
        return collator.compare(leadSortName(b), leadSortName(a));
      case 'statusAsc': {
        const byStatus =
          (STATUS_SORT_ORDER[a.status] ?? 99) -
          (STATUS_SORT_ORDER[b.status] ?? 99);
        if (byStatus !== 0) return byStatus;
        return createdAtMs(b) - createdAtMs(a);
      }
      default:
        return 0;
    }
  });
}
